import AddIcon from "@mui/icons-material/Add";
import BiotechOutlinedIcon from "@mui/icons-material/BiotechOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { Divider, Typography } from "@mui/material";
import moment from "moment";
import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import CustomButton from "../../../components/CustomButton";
import CustomModal from "../../../components/CustomModal/CustomModal";
import {
  createPrescriptionWoLoader,
  deleteDiagnosticsById,
  deleteProcedureById,
  getPetDiagnosticsWoLoader,
  getPetsCompliantSummaryWoLoader,
  getProceduresByPetIdWoLoader,
  getReminderProceduresByPetIdWoLoader,
  updateDiagnostics,
  updatePrescriptionByIdWoLoader,
  updateProcedureById,
} from "../../../redux/reducers/petSlice";
import { AppColors } from "../../../util/AppColors";
import {
  extractPrescriptionDetails,
  getOnlyName,
  getProcedureDetailsNew,
} from "../../../util/function";
import { canWriteModule } from "../../../util/roleModuleAccess";
import CreateUpdateReminder from "../Reminder/CreateUpdateReminder";

const REMINDER_TABS = [
  { id: "all", label: "All", count: 8 },
  { id: "today", label: "Due", count: 3 },
  { id: "overdue", label: "Overdue", count: 2 },
];

const getTodayStr = () => moment().format("YYYY-MM-DD");

const isPreventiveProcedure = (item) => item?.isFromPreventive === true;

const getProcedureDueStatus = (dueDateValue) => {
  if (!dueDateValue) {
    return { statusTone: "upcoming", status: "Due" };
  }
  const dueDate = moment(dueDateValue);
  if (!dueDate.isValid()) {
    return { statusTone: "upcoming", status: "Due" };
  }
  const today = getTodayStr();
  const dueDateStr = dueDate.format("YYYY-MM-DD");

  if (dueDateStr < today) {
    return {
      statusTone: "overdue",
      status: `Overdue: ${dueDate.format("D MMM YYYY")}`,
    };
  }
  if (dueDateStr === today) {
    return { statusTone: "today", status: "Due today" };
  }
  const daysUntilDue = dueDate.diff(moment(), "days");
  return {
    statusTone: "upcoming",
    status:
      daysUntilDue > 0
        ? `Due: ${dueDate.format("D MMM YYYY")} (in ${daysUntilDue} days)`
        : `Due: ${dueDate.format("D MMM YYYY")}`,
  };
};

const getReminderStatusClassName = (statusTone) => {
  if (statusTone === "today") return "card-head-orange-color";
  if (statusTone === "overdue") return "red8";
  return "";
};

const withDueDate = (item, dueDate) => ({ ...item, __dueDate: dueDate });

const mapProcedureToDisplayItem = (item) => {
  const { status, statusTone } = getProcedureDueStatus(item?.__dueDate);
  return {
    id: item?.procedureId,
    title: item?.procedureName ?? item?.planItem,
    status,
    statusTone,
    raw: item,
  };
};

const mapDiagnosticToDisplayItem = (item) => {
  const { status, statusTone } = getProcedureDueStatus(item?.__dueDate);
  return {
    id: item?.diagonosticId,
    title: item?.testName,
    status,
    statusTone,
    raw: item,
  };
};

const filterRemindersByTab = (list, activeTab) => {
  const today = getTodayStr();

  if (activeTab === "all") return list;

  if (activeTab === "overdue") {
    return list.filter((li) => {
      const dueDate = li?.__dueDate;
      return dueDate && moment(dueDate).format("YYYY-MM-DD") < today;
    });
  }

  return list.filter((li) => {
    const dueDate = li?.__dueDate;
    return !dueDate || moment(dueDate).format("YYYY-MM-DD") >= today;
  });
};

const getReminderTabCounts = (sourceList) => {
  const today = getTodayStr();
  const overdueCount = sourceList.filter(
    (li) => li?.__dueDate && moment(li.__dueDate).format("YYYY-MM-DD") < today,
  ).length;
  const dueCount = sourceList.filter(
    (li) =>
      !li?.__dueDate || moment(li.__dueDate).format("YYYY-MM-DD") >= today,
  ).length;
  return {
    all: sourceList.length,
    today: dueCount,
    overdue: overdueCount,
  };
};

const isUnscheduledDiagnostic = (item) => !item?.appoinment?.appointmentId;

const isActivePrescription = (item) =>
  moment(item?.endDate).isSameOrAfter(moment().startOf("day"), "day");

const getMedicationSubStatus = (item) => {
  if (item?.quantity != null && item?.subUnit) {
    return `${item.quantity} ${item.subUnit}`;
  }
  if (item?.dosage && item?.subUnit) {
    return `${item.dosage} ${item.subUnit}`;
  }
  return item?.dosage || null;
};

const mapMedicationToDisplayItem = (item) => {
  const { status, statusTone } = getProcedureDueStatus(item?.__dueDate);
  return {
    id: item?.id,
    title: item?.name,
    status,
    subStatus: getMedicationSubStatus(item),
    statusTone,
    raw: item,
  };
};

const getRequiredPresData = (prescriptionsData) =>
  extractPrescriptionDetails(prescriptionsData ?? []).filter(
    isActivePrescription,
  );

const initValues = {
  preventive: true,
  diagnostics: true,
  medications: true,
  care: true,
};

const REMINDER_CATEGORY_TYPES = {
  preventive: "Preventive",
  diagnostics: "Diagnostics",
  medications: "Medications",
  care: "Care",
};

const ReminderItemActions = ({
  onEdit,
  onAddToAppointment,
  onDelete,
  apntId,
  serviceId,
}) => {
  const canManageDashboard = canWriteModule("dashboard");
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuRect, setMenuRect] = useState(null);

  const handleCloseMenu = () => {
    setMenuOpen(false);
    setMenuRect(null);
  };

  const handleToggleMenu = (event) => {
    event.stopPropagation();
    if (menuOpen) {
      handleCloseMenu();
      return;
    }
    const rect = event.currentTarget?.getBoundingClientRect?.();
    if (!rect) return;
    setMenuRect(rect);
    setMenuOpen(true);
  };

  const renderMenuPortal = () => {
    if (!menuOpen || !menuRect) return null;

    return createPortal(
      <div className="new-cal-menu-overlay">
        <div
          className="new-cal-menu-backdrop"
          onClick={handleCloseMenu}
          aria-hidden="true"
        />
        <div
          className="new-cal-menu new-cal-menu-portal"
          style={{
            position: "fixed",
            zIndex: 1400,
            right: window.innerWidth - menuRect.right,
            top: menuRect.bottom + 4,
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <Typography
            className="font-medium fs14 ph10 pv5 cursor txt-ali-left"
            onClick={() => {
              onEdit();
              handleCloseMenu();
            }}
          >
            Edit
          </Typography>
          {apntId && serviceId ? (
            <>
              <Divider />
              <Typography
                className="font-medium fs14 ph10 pv5 cursor txt-ali-left"
                onClick={() => {
                  onAddToAppointment();
                  handleCloseMenu();
                }}
              >
                Add to appointment
              </Typography>
            </>
          ) : null}
          <Divider />
          <Typography
            className="font-medium fs14 ph10 pv5 cursor txt-ali-left"
            onClick={() => {
              onDelete();
              handleCloseMenu();
            }}
          >
            Delete
          </Typography>
        </div>
      </div>,
      document.body,
    );
  };

  return (
    <>
      {canManageDashboard ? (
        <MoreVertIcon
          className="new-cal-apnt-menu-trigger"
          sx={{
            height: 20,
            cursor: "pointer",
            transition: "color 0.3s ease",
            ":hover": { color: "#1976d2" },
          }}
          onClick={handleToggleMenu}
        />
      ) : null}
      {renderMenuPortal()}
    </>
  );
};

const ReminderCategorySection = ({
  categoryKey,
  isOpen,
  onToggle,
  headerIcon,
  title,
  titleClassName,
  displayList,
  onEditReminder,
  onAddToAppointment,
  onDeleteReminder,
  apntId,
}) => (
  <div className="pet-rem-category-section">
    <div className="flex-row-ali-cen cursor" onClick={onToggle}>
      <div className="flex-row-ali-cen pl10">
        {headerIcon}
        <Typography className={`fs13 font-bold ml5 ${titleClassName}`}>
          {title}
        </Typography>
      </div>
      <div className="flex1-end pr5">
        {isOpen ? (
          <KeyboardArrowDownIcon className="gray4" sx={{ fontSize: 20 }} />
        ) : (
          <ChevronRightIcon className="gray4" sx={{ fontSize: 20 }} />
        )}
      </div>
    </div>
    {isOpen ? (
      <>
        <div className="pet-rem-item-divider mt5" />
        {displayList?.slice(0, 3)?.map((item, index) => (
          <div key={item.id}>
            {index > 0 ? <div className="pet-rem-item-divider" /> : null}
            <div className="pet-rem-category-item flex-row-ali-cen">
              <div className="pet-rem-item-content flex1">
                <Typography className="fs12 font-bold black">
                  {item?.title ?? ""}
                </Typography>
                <Typography
                  className={`fs11 pet-rem-item-status ${getReminderStatusClassName(item.statusTone)}`}
                >
                  {item.status}
                  {item.subStatus ? (
                    <span className="pet-rem-item-substatus">
                      {" "}
                      · {item.subStatus}
                    </span>
                  ) : null}
                </Typography>
              </div>
              <ReminderItemActions
                onEdit={() => onEditReminder(item, categoryKey)}
                onAddToAppointment={() => onAddToAppointment(item, categoryKey)}
                onDelete={() => onDeleteReminder(item, categoryKey)}
                apntId={apntId}
                serviceId={item?.raw?.serviceId}
              />
            </div>
          </div>
        ))}
      </>
    ) : null}
  </div>
);

const defObj = {
  clinic: {},
  services: [],
  stocks: [],
  facilities: [],
  locations: [],
};

const PetRemindersSidebar = ({ onOpenReminders, apntId: apntIdProp }) => {
  const dispatch = useDispatch();
  const canManageDashboard = canWriteModule("dashboard");
  const location = useLocation();
  const { appointment } = location.state || {};
  const [activeTab, setActiveTab] = useState("all");
  const [tabOpens, setTabOpens] = useState(initValues);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemType, setItemType] = useState(null);
  const [confirmModVsble, setConfirmModVsble] = useState(false);
  const [delModVsble, setDelModVsble] = useState(false);
  const [addToApntMode, setAddToApntMode] = useState(false);
  const [modVsble, setModVsble] = useState(false);
  const { procedure, reminderProcedure, diagnostics, prescriptions } =
    useSelector((state) => state?.pet);
  // const { locationList, facilities } = useSelector((state) => state?.clinic);
  const apntDet = useSelector((state) => state?.pet?.complaintSummary?.data);
  const appDet = apntDet?.appointment;
  const petId = appointment?.appoinment?.petId;
  const apntId = apntIdProp ?? appointment?.appoinment?.appointmentId;
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const { services, stocks, clinic, facilities, locations } = useMemo(() => {
    if (!mandateDataRaw) return defObj;
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        clinic: reqData?.clinic ?? {},
        services: Array.isArray(reqData?.services) ? reqData?.services : [],
        stocks: Array.isArray(reqData?.stocks) ? reqData?.stocks : [],
        facilities: Array.isArray(reqData?.facilities)
          ? reqData?.facilities
          : [],
        locations: Array.isArray(reqData?.locations) ? reqData?.locations : [],
      };
    } catch {
      return defObj;
    }
  }, [mandateDataRaw]);

  const preventiveSourceList = useMemo(
    () =>
      (procedure?.data ?? [])
        .filter(isPreventiveProcedure)
        .map((item) => withDueDate(item, item?.doneAt)),
    [procedure?.data],
  );

  const diagnosticsSourceList = useMemo(
    () =>
      (diagnostics?.data ?? [])
        .filter(isUnscheduledDiagnostic)
        .map((item) => withDueDate(item, item?.dueDate)),
    [diagnostics?.data],
  );

  const generalSourceList = useMemo(
    () =>
      (reminderProcedure?.data ?? []).map((item) =>
        withDueDate(item, item?.doneAt),
      ),
    [reminderProcedure?.data],
  );

  const medicationsSourceList = useMemo(
    () =>
      getRequiredPresData(prescriptions?.data).map((item) =>
        withDueDate(item, item?.startDate),
      ),
    [prescriptions?.data],
  );

  const allReminderSourceList = useMemo(
    () => [
      ...preventiveSourceList,
      ...diagnosticsSourceList,
      // ...medicationsSourceList,
      ...generalSourceList,
    ],
    [
      preventiveSourceList,
      diagnosticsSourceList,
      // medicationsSourceList,
      generalSourceList,
    ],
  );

  const preventiveList = useMemo(
    () => filterRemindersByTab(preventiveSourceList, activeTab),
    [preventiveSourceList, activeTab],
  );

  const diagnosticsList = useMemo(
    () => filterRemindersByTab(diagnosticsSourceList, activeTab),
    [diagnosticsSourceList, activeTab],
  );

  const generalList = useMemo(
    () => filterRemindersByTab(generalSourceList, activeTab),
    [generalSourceList, activeTab],
  );

  const medicationsList = useMemo(
    () => filterRemindersByTab(medicationsSourceList, activeTab),
    [medicationsSourceList, activeTab],
  );

  const preventiveDisplayList = useMemo(
    () => preventiveList.map(mapProcedureToDisplayItem),
    [preventiveList],
  );

  const diagnosticsDisplayList = useMemo(
    () => diagnosticsList.map(mapDiagnosticToDisplayItem),
    [diagnosticsList],
  );

  const generalDisplayList = useMemo(
    () => generalList.map(mapProcedureToDisplayItem),
    [generalList],
  );

  const medicationsDisplayList = useMemo(
    () => medicationsList.map(mapMedicationToDisplayItem),
    [medicationsList],
  );

  const reminderTabCounts = useMemo(
    () => getReminderTabCounts(allReminderSourceList),
    [allReminderSourceList],
  );

  const hasPreventiveItems = preventiveDisplayList?.length > 0;
  const hasDiagnosticsItems = diagnosticsDisplayList?.length > 0;
  const hasGeneralItems = generalDisplayList?.length > 0;

  const visibleReminderCategories = useMemo(() => {
    const categories = [];

    if (hasPreventiveItems) {
      categories.push({
        key: "preventive",
        title: `Preventive (${preventiveDisplayList.length})`,
        titleClassName: "green-color",
        headerIcon: (
          <ShieldOutlinedIcon sx={{ fontSize: 18, color: AppColors.green }} />
        ),
        displayList: preventiveDisplayList,
      });
    }

    if (hasDiagnosticsItems) {
      categories.push({
        key: "diagnostics",
        title: `Diagnostics (${diagnosticsDisplayList.length})`,
        titleClassName: "blue-color",
        headerIcon: (
          <BiotechOutlinedIcon
            sx={{ fontSize: 18, color: AppColors.appPrimary }}
          />
        ),
        displayList: diagnosticsDisplayList,
      });
    }

    if (hasGeneralItems) {
      categories.push({
        key: "care",
        title: `Care (${generalDisplayList.length})`,
        titleClassName: "rose",
        headerIcon: (
          <MedicationOutlinedIcon
            sx={{ fontSize: 18, color: AppColors.rose }}
          />
        ),
        displayList: generalDisplayList,
      });
    }

    return categories;
  }, [
    hasPreventiveItems,
    hasDiagnosticsItems,
    hasGeneralItems,
    preventiveDisplayList,
    diagnosticsDisplayList,
    generalDisplayList,
  ]);

  const handleOpenCollapse = (name, value) => {
    setTabOpens({ ...tabOpens, [name]: value });
  };

  const onOpenCreateUpdateModal = (
    itm = null,
    typ = null,
    addToApnt = false,
  ) => {
    setSelectedItem(itm);
    setItemType(typ);
    setAddToApntMode(addToApnt);
    setModVsble(true);
  };

  const onCloseMod = (typ, catType) => {
    if (typ === "apiSuccess") {
      if (catType === "Preventive") {
        dispatch(
          getProceduresByPetIdWoLoader({
            petId: petId,
            url: `?reminderPreventive=true&page=1&itemSize=50`,
          }),
        );
      }
      if (catType === "Care") {
        dispatch(
          getReminderProceduresByPetIdWoLoader({
            petId: petId,
            url: `?reminderProcedure=true&page=1&itemSize=50`,
          }),
        );
      }
      if (catType === "Diagnostics") {
        dispatch(
          getPetDiagnosticsWoLoader({
            petId: petId,
            url: "?page=1&itemSize=50",
          }),
        );
      }
    }
    setModVsble(false);
    setSelectedItem(null);
    setItemType(null);
    setAddToApntMode(false);
  };

  const handleEditReminder = (item, categoryKey) => {
    if (categoryKey === "medications") {
      onOpenReminders?.();
      return;
    }
    onOpenCreateUpdateModal(item?.raw, REMINDER_CATEGORY_TYPES[categoryKey]);
  };

  const handleAddToAppointment = (item, categoryKey) => {
    // if (categoryKey === "medications") {
    //   onOpenReminders?.();
    //   return;
    // }
    setSelectedItem(item);
    setItemType(REMINDER_CATEGORY_TYPES[categoryKey]);
    setConfirmModVsble(true);
    // onOpenCreateUpdateModal(null, REMINDER_CATEGORY_TYPES[categoryKey], true);
  };

  const handleDeleteReminder = (item, categoryKey) => {
    if (categoryKey === "medications") {
      onOpenReminders?.();
      return;
    }
    setSelectedItem(item);
    setItemType(REMINDER_CATEGORY_TYPES[categoryKey]);
    setDelModVsble(true);
  };

  const onCloseDelMod = () => {
    setDelModVsble(false);
    setSelectedItem(null);
    setItemType(null);
  };

  const onConfirmDelete = () => {
    const reqData = selectedItem?.raw;
    if (itemType === "Preventive" || itemType === "Care") {
      if (!reqData?.procedureId) return;
      dispatch(deleteProcedureById(reqData.procedureId)).then((res) => {
        if (res?.payload) {
          onCloseMod("apiSuccess", itemType);
          if (apntId) {
            dispatch(getPetsCompliantSummaryWoLoader(apntId));
          }
          onCloseDelMod();
        }
      });
      return;
    }
    if (itemType === "Diagnostics") {
      if (!reqData?.diagonosticId) return;
      dispatch(deleteDiagnosticsById(reqData.diagonosticId)).then((res) => {
        if (res?.payload) {
          onCloseMod("apiSuccess", itemType);
          if (apntId) {
            dispatch(getPetsCompliantSummaryWoLoader(apntId));
          }
          onCloseDelMod();
        }
      });
    }
  };

  const onCloseConMod = () => {
    setConfirmModVsble(false);
    setSelectedItem(null);
    setItemType(null);
  };

  const handleModClose = (typ, cat) => {
    onCloseMod(typ, cat);
    if (typ === "apiSuccess" && apntId) {
      dispatch(getPetsCompliantSummaryWoLoader(apntId));
    }
    onCloseConMod();
  };

  const onConfirmAddToAppointment = async () => {
    const reqData = selectedItem?.raw;
    if (itemType === "Preventive" || itemType === "Care") {
      const data = {
        doneAt: moment(new Date(reqData?.doneAt)).format("YYYY-MM-DD"),
        remarks: reqData?.remarks,
        procedureDetails: [],
        isFromPreventive: itemType === "Preventive",
        planItem: reqData?.planItem ?? "",
      };
      if (reqData?.serviceType) data.serviceType = reqData?.serviceType;
      if (reqData?.procedureVariantId) {
        data.procedureVariantId = reqData?.procedureVariantId;
      }
      data.appointmentId = apntId;
      const procedureDetailsResult = await getProcedureDetailsNew({
        servId: reqData?.serviceId,
        varntId: reqData?.procedureVariantId,
        services: services ?? [],
        clinicStocks: stocks ?? [],
        pharmacyTaxId: clinic?.pharmacyTaxId,
        status: true,
        facilities: facilities ?? [],
        facilityId: appDet?.facilityId,
        patientWorkbook: true,
        locations: locations ?? [],
        clinicId: clinic?.id,
      });
      const procedureDetails = procedureDetailsResult?.procedureDetails;
      data.procedureDetails = procedureDetails ?? [];
      if (procedureDetailsResult?.prescriptions?.length > 0) {
        const prescriptionData = {
          status: "Pending",
          remark: "",
          prescription: procedureDetailsResult?.prescriptions,
          issue: "",
          paymentId: apntDet?.paymentId ?? null,
        };
        if (apntDet?.prescription?.[0]?.prescriptionId) {
          dispatch(
            updatePrescriptionByIdWoLoader({
              prescriptionId: apntDet?.prescription?.[0]?.prescriptionId,
              data: prescriptionData,
            }),
          );
        } else {
          dispatch(
            createPrescriptionWoLoader({
              appointmentId: appDet?.appointmentId,
              data: prescriptionData,
            }),
          );
        }
      }
      const resServ = services?.find(
        (ser) => ser?.serviceId === reqData?.serviceId,
      );
      const reqVar = resServ?.serviceVariants?.find(
        (vnt) => vnt?.variantId === reqData?.procedureVariantId,
      );
      let tax = 0;
      let total = reqVar?.servicePrice ?? 0;
      if (resServ?.taxApplicability?.toLowerCase() === "yes") {
        tax +=
          resServ?.taxApplicability?.toLowerCase() === "yes"
            ? (Number(reqVar?.servicePrice ?? 0) / 100) *
              Number(resServ?.tax ?? 0)
            : 0;
        total += tax;
      }
      const reqQty = 1;
      data.procedureName = resServ?.serviceName;
      data.serviceId = resServ?.serviceId;
      data.category = resServ?.category;
      data.subCategory = resServ?.subCategory;
      data.petId = appDet?.petId;
      data.appointmentId = appDet?.appointmentId;
      data.serviceCharge = reqVar?.servicePrice ?? 0;
      data.serviceType = getOnlyName(reqVar?.variantName);
      data.tax = Number(Number(tax) * Number(reqQty)) ?? 0;
      data.total = Number(Number(total) * Number(reqQty)) ?? 0;
      data.mrpPrice = Number(reqVar?.servicePrice ?? 0);
      data.quantity = Number(reqQty);
      data.patientWorkbook = true;
      data.mainProcedure = true;
      data.hsnSacCode = resServ?.hsnSacCode ?? null;
      data.paymentId = apntDet?.paymentId ?? null;
      dispatch(updateProcedureById({ id: reqData?.procedureId, data })).then(
        (res) => {
          if (res?.payload) handleModClose("apiSuccess", itemType);
        },
      );
    }
    if (itemType === "Diagnostics") {
      const reqserv = services?.find(
        (ser) => ser?.serviceId === reqData?.serviceId,
      );
      const reqVar = reqserv?.serviceVariants?.find(
        (sv) => sv?.variantId === reqData?.variantId,
      );
      let tax = 0;
      let total = reqVar?.servicePrice ?? 0;
      if (reqserv?.taxApplicability?.toLowerCase() === "yes") {
        tax +=
          reqserv?.taxApplicability?.toLowerCase() === "yes"
            ? (Number(reqVar?.servicePrice ?? 0) / 100) *
              Number(reqserv?.tax ?? 0)
            : 0;
        total += tax;
      }
      const form = new FormData();
      form.append("appId", apntId);
      form.append("type", reqData?.type);
      form.append("testName", reqData?.testName);
      form.append("sampleCollected", false);
      form.append("price", Number(total ?? 0));
      form.append("tax", Number(tax ?? 0));
      form.append(
        "dueDate",
        moment(new Date(reqData?.dueDate)).format("YYYY-MM-DD"),
      );
      form.append("remark", reqData?.remark);
      form.append("hsnSacCode", Number(reqserv?.hsnSacCode ?? null));
      form.append("status", "Scheduled");
      if (reqserv?.serviceId) {
        form.append("serviceId", reqserv?.serviceId);
      }
      if (reqData?.variantId) {
        form.append("variantId", reqData?.variantId);
      }
      dispatch(
        updateDiagnostics({ diagnosticsId: reqData?.diagonosticId, form }),
      ).then((res) => {
        if (res?.payload) handleModClose("apiSuccess", itemType);
      });
    }
  };

  return (
    <>
      <div className="pet-rem-sidebar">
        <div className="pet-rem-header flex-row-between-align-center mb10">
          <Typography className="clr-muted fs11 txt-semi-bold uppercase">
            Reminders
          </Typography>
          {canManageDashboard ? (
            <button
              type="button"
              className="pet-rem-add-btn flex-row-ali-cen cursor"
              onClick={() => onOpenCreateUpdateModal()}
            >
              <AddIcon sx={{ fontSize: 16 }} />
              <span>Add</span>
            </button>
          ) : null}
        </div>

        <div className="pet-rem-tabs flex-row-ali-cen mb12">
          {REMINDER_TABS?.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`pet-rem-tab cursor ${
                activeTab === tab?.id ? "pet-rem-tab-active" : ""
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span>{tab.label}</span>
              <span
                className={`pet-rem-tab-count ${
                  tab.id === "all"
                    ? "pet-rem-tab-count-blue"
                    : "pet-rem-tab-count-red"
                }`}
              >
                {reminderTabCounts[tab?.id] ?? tab.count}
              </span>
            </button>
          ))}
        </div>

        <div className="pet-rem-category-list-wrap">
          {visibleReminderCategories.length > 0 ? (
            <div className="pet-rem-category-card br8 mt5 pv5">
              {visibleReminderCategories.map((category, index) => (
                <div key={category.key}>
                  {index > 0 ? (
                    <div className="pet-rem-category-separator" />
                  ) : null}
                  <ReminderCategorySection
                    categoryKey={category.key}
                    isOpen={tabOpens?.[category.key]}
                    onToggle={() =>
                      handleOpenCollapse(
                        category.key,
                        !tabOpens?.[category.key],
                      )
                    }
                    headerIcon={category.headerIcon}
                    title={category?.title}
                    titleClassName={category.titleClassName}
                    displayList={category.displayList}
                    onEditReminder={handleEditReminder}
                    onAddToAppointment={handleAddToAppointment}
                    onDeleteReminder={handleDeleteReminder}
                    apntId={apntId ?? null}
                  />
                </div>
              ))}
              <button
                type="button"
                className="pet-rem-category-footer flex-row-ali-cen cursor w100Per"
                onClick={onOpenReminders}
              >
                <span className="fs12 font-medium blue-color">View all</span>
                <div className="flex1-end">
                  <ChevronRightIcon
                    className="blue-color ml4"
                    sx={{ fontSize: 16 }}
                  />
                </div>
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {modVsble ? (
        <CreateUpdateReminder
          modVsble={modVsble}
          onCloseMod={onCloseMod}
          selectedItem={selectedItem}
          selecetdtype={itemType}
          addToAppointment={addToApntMode}
        />
      ) : null}

      <CustomModal
        open={confirmModVsble}
        onClose={onCloseConMod}
        header="Add to Appointment"
        modal
        modalWidth={40}
      >
        <Typography className="txt-regular fs16 ml10">
          Are you sure you want to add it to the appointment?
        </Typography>
        <div className="flex-row flex-center mv20">
          <div>
            <CustomButton
              text="Yes"
              redBtn
              onClick={onConfirmAddToAppointment}
              smallBtn
            />
          </div>
          <div className="ml20">
            <CustomButton text="No" onClick={onCloseConMod} smallBtn />
          </div>
        </div>
      </CustomModal>

      <CustomModal
        open={delModVsble}
        onClose={onCloseDelMod}
        header="Delete"
        modal
        modalWidth={40}
      >
        <Typography className="txt-regular fs16 ml10">
          Are you sure you want to delete this record?
        </Typography>
        <div className="flex-row flex-center mv20">
          <div>
            <CustomButton
              text="Yes"
              redBtn
              onClick={onConfirmDelete}
              smallBtn
            />
          </div>
          <div className="ml20">
            <CustomButton text="No" onClick={onCloseDelMod} smallBtn />
          </div>
        </div>
      </CustomModal>
    </>
  );
};

export default PetRemindersSidebar;
